// Corpus-free category definitions — safe for client bundles.
export type PcFaqCategory =
  | "apostille"
  | "documents"
  | "cost"
  | "process"
  | "urgent"
  | "abroad"
  | "visa"
  | "areas";

export interface CategoryDef {
  slug: PcFaqCategory;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  matchTh: RegExp;
  matchEn: RegExp;
}

export const PC_FAQ_CATEGORIES: CategoryDef[] = [
  {
    slug: "apostille",
    titleTh: "Apostille & MFA รับรอง",
    titleEn: "Apostille & MFA Legalization",
    descTh: "การรับรองใบตำรวจ MFA และ Apostille 2026 สำหรับใช้ต่างประเทศ",
    descEn: "Apostille (Hague 2026) and MFA legalization of Thai police clearance.",
    matchTh: /(apostille|hague|กระทรวงการต่างประเทศ|กงสุล|mfa|รับรอง)/i,
    matchEn: /(apostille|hague|mfa|legaliz|consular|ministry of foreign)/i,
  },
  {
    slug: "documents",
    titleTh: "เอกสารและลายนิ้วมือ",
    titleEn: "Documents & Fingerprints",
    descTh: "รายการเอกสาร แบบฟอร์ม POA และการพิมพ์ลายนิ้วมือ FD-258",
    descEn: "Required documents, POA form, and FD-258 fingerprinting.",
    matchTh: /(เอกสาร|แบบฟอร์ม|ลายนิ้วมือ|fd-258|fingerprint|passport|poa|มอบอำนาจ)/i,
    matchEn: /(document|form|fingerprint|fd-258|poa|power of attorney|passport)/i,
  },
  {
    slug: "cost",
    titleTh: "ราคาและค่าธรรมเนียม",
    titleEn: "Pricing & Fees",
    descTh: "ค่าธรรมเนียมราชการ ค่าเอเจนซี่ และ DHL ระหว่างประเทศ",
    descEn: "Government fee, agency package pricing, and international DHL.",
    matchTh: /(ราคา|ค่าบริการ|ค่าธรรมเนียม|กี่บาท|dhl|บาท)/,
    matchEn: /(price|cost|fee|how much|thb|baht|dhl)/i,
  },
  {
    slug: "process",
    titleTh: "ขั้นตอนและระยะเวลา",
    titleEn: "Process & Timeline",
    descTh: "ขั้นตอนขอใบรับรองความประพฤติจาก PCSC ครบทุกช่วง ทั้งการจองคิว การพิมพ์ลายนิ้วมือในไทยและต่างจังหวัด เอกสารมอบอำนาจ และระยะเวลาที่ใช้จริงในแต่ละกรณี",
    descEn: "Every stage of a Thai police clearance certificate from PCSC: queue booking, fingerprinting in Bangkok or upcountry, power of attorney for remote applicants, and the real processing times per case type.",
    matchTh: /(ขั้นตอน|กี่วัน|ระยะเวลา|ใช้เวลา|pcsc|วิธี)/i,
    matchEn: /(process|step|how long|timeline|procedure|pcsc|how do i|how to)/i,
  },
  {
    slug: "urgent",
    titleTh: "บริการด่วน / Fast Track",
    titleEn: "Fast Track / Urgent",
    descTh: "Fast Track 5–7 วัน และกรณีเร่งด่วนสำหรับวีซ่า",
    descEn: "5–7 day fast-track and urgent visa cases.",
    matchTh: /(ด่วน|เร่งด่วน|fast|เร่ง)/,
    matchEn: /(urgent|fast.?track|rush|expedite|express)/i,
  },
  {
    slug: "abroad",
    titleTh: "คนไทย/ต่างชาติในต่างประเทศ",
    titleEn: "From Overseas",
    descTh: "ยื่นจากต่างประเทศ POA สถานทูตไทย และ FD-258",
    descEn: "Applying from overseas — Thai embassy POA and FD-258 workflow.",
    matchTh: /(ต่างประเทศ|สถานทูต|ต่างแดน|overseas|abroad|มอบอำนาจ)/i,
    matchEn: /(abroad|overseas|embassy|thai consul|former expat|from overseas)/i,
  },
  {
    slug: "visa",
    titleTh: "ประเทศปลายทาง & วีซ่า",
    titleEn: "Destination Country & Visa",
    descTh: "ข้อกำหนดตามประเทศ — US, UK, AU, CA, EU, Japan, China, UAE",
    descEn: "Per-country requirements — US, UK, Australia, Canada, EU, Japan.",
    matchTh: /(อเมริกา|อังกฤษ|ออสเตรเลีย|แคนาดา|เชงเก้น|วีซ่า|เยอรมัน|ญี่ปุ่น|จีน|เกาหลี|uae|qatar)/i,
    matchEn: /(usa|uscis|uk\b|ukvi|australia|immiaccount|canada|ircc|schengen|germany|japan|china|korea|uae|qatar|visa|pr\b|citizenship)/i,
  },
  {
    slug: "areas",
    titleTh: "ยื่นจากต่างจังหวัด / รายพื้นที่",
    titleEn: "By Province & Upcountry Filing",
    descTh: "ภูเก็ต เชียงใหม่ พัทยา หาดใหญ่ อีสาน และเกาะ — พิมพ์ลายนิ้วมือในพื้นที่ + มอบอำนาจยื่นที่กรุงเทพฯ",
    descEn: "Phuket, Chiang Mai, Pattaya, Hat Yai, Isan and islands — local fingerprinting with POA filing in Bangkok.",
    matchTh: /(ภูเก็ต|เชียงใหม่|เชียงราย|พัทยา|ชลบุรี|หาดใหญ่|สงขลา|อุดรธานี|ขอนแก่น|นครราชสีมา|ระยอง|สมุย|พะงัน|เกาะช้าง|กระบี่|พังงา|สุราษฎร์|นครศรีธรรมราช|ต่างจังหวัด|ในพื้นที่)/,
    matchEn: /(phuket|chiang mai|chiang rai|pattaya|chonburi|hat yai|udon|khon kaen|nakhon|rayong|samui|phangan|koh chang|krabi|phang nga|surat thani|upcountry|province)/i,
  },
];

export function getPcCategoryDef(slug: string): CategoryDef | undefined {
  return PC_FAQ_CATEGORIES.find((c) => c.slug === slug);
}
